import Image from 'next/image';
import Link from 'next/link';
import { Star } from 'lucide-react';
import { heroData } from '../data/heroData';

export default function Hero({
  category,
  page,
  title: propTitle,
  subtitle: propSubtitle,
  button: propButton,
  buttonLink,
  backgroundImage: propBg,
  hideButton = false,
  hasContentH1 = false
}) {

  const fallbackData = heroData[category] || heroData.home;

  const title = propTitle || fallbackData.title;
  const subtitle = propSubtitle || fallbackData.subtitle;
  const button = propButton !== undefined
    ? propButton
    : fallbackData.button;

  const backgroundImage =
    propBg || fallbackData.backgroundImage;

  const buttonHref =
    buttonLink?.trim() || 'tel:+13233004130';

  const TitleTag = hasContentH1 ? 'div' : 'h1';

  const sectionClass =
    page === 'home'
      ? 'relative h-[100vh] w-full overflow-hidden'
      : 'relative h-[500px] w-full overflow-hidden';

  const overlayClass =
    'absolute inset-0 bg-gradient-to-t from-black/75 via-black/45 to-black/20';

  const titleClass = `
    text-white
    ${page === 'home' ? 'mt-0' : 'mt-20'}
    text-lg
    leading-[2.0em]
    tracking-[8px]
    lg:text-4xl
    lg:leading-[1.5em]
    tracking-[6px]
    lg:tracking-[10px]
    font-light
    font-gilda
    uppercase
    mb-4
    min-h-[110px]
  `;

  const parsedTitle = Array.isArray(title)
    ? title.map((line, i) => (
        <span key={i} className={i > 0 ? 'block' : ''}>
          {line.replace(/<br\s*\/?>/gi, '')}
        </span>
      ))
    : <span>{title}</span>;

  const parsedSubtitle = subtitle
    .split(/<br\s*\/?>/gi)
    .map((line, i) => (
      <span key={i} className={i > 0 ? 'block' : ''}>
        {line.trim()}
      </span>
    ));

  return (
    <section className={sectionClass}>
      <Image
        src={backgroundImage}
        alt={Array.isArray(title) ? title.join(' ') : title}
        title={Array.isArray(title) ? title.join(' ') : title}
        fill
        priority={page === 'home'}
        loading={page === 'home' ? 'eager' : 'lazy'}
        sizes="(max-width: 768px) 100vw, 1920px"
        quality={page === 'home' ? 50 : 45}
        style={{
          objectFit: 'cover',
          objectPosition: 'center'
        }}
      />

      <div className={overlayClass} />

      <div className="relative z-10 h-full flex flex-col justify-center items-center text-center px-4">

        {page === 'home' && (
          <div
            className="flex mb-5"
            role="img"
            aria-label="5 star rating"
          >
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className="w-6 h-6 mr-2 fill-primary text-transparent"
                aria-hidden="true"
              />
            ))}
          </div>
        )}

        <TitleTag
          className={titleClass}
          role={hasContentH1 ? 'heading' : undefined}
          aria-level={hasContentH1 ? 1 : undefined}
        >
          {parsedTitle}
        </TitleTag>

        <p className="text-white uppercase tracking-[6px] font-light text-xs leading-[1.5em] lg:text-lg lg:leading-[1.5em] max-w-3xl mb-8 min-h-[70px]">
          {parsedSubtitle}
        </p>

        <div className="min-h-[64px] flex items-center justify-center">
          {button && !hideButton && (
            <Link href={buttonHref} prefetch={false}>
              <span className="relative inline-block px-6 py-3 text-lg uppercase tracking-wider border border-white text-white group overflow-hidden">
                <span className="relative z-10 font-light group-hover:text-black">
                  {button}
                </span>

                <span className="absolute inset-0 bg-white left-0 h-full w-0 transition-all duration-300 ease-in-out group-hover:w-full" />
              </span>
            </Link>
          )}
        </div>

      </div>
    </section>
  );
}